import { Range } from './Range'

type ReducerToken = {
  depth: number
  inExpression: boolean
  inComment: boolean
  foundExpressions: Range[]
  lastChar: string
  entry: number
  str: string
}

export type FindExpressionsOptions = {
  /**
   * Stop evaluating after this number of expressions are found.
   */
  maxExpressions?: number
  verbose?: boolean
}

export const findExpressions = (
  value: string,
  options?: FindExpressionsOptions
): Range[] => {
  const { verbose, maxExpressions } = options ?? {}

  return Array.from(value).reduce(
    (acc: ReducerToken, char, i): ReducerToken => {
      if (
        typeof maxExpressions === 'number' &&
        acc.foundExpressions.length >= maxExpressions
      ) {
        return acc
      }

      if (acc.lastChar === '\\') {
        acc.lastChar = '_'
        if (verbose) {
          console.log({ i, char, lastChar: acc.lastChar }, 'Escaped')
        }
        return acc
      }

      if (acc.inComment && char === '/' && acc.lastChar === '*') {
        acc.lastChar = char
        acc.inComment = false
        if (verbose) {
          console.log({ i, char, lastChar: acc.lastChar }, 'Comment ended')
        }
        return acc
      }

      if (acc.inExpression && char === '*' && acc.lastChar === '/') {
        acc.lastChar = char
        acc.inComment = true
        if (verbose) {
          console.log({ i, char, lastChar: acc.lastChar }, 'Comment started')
        }
        return acc
      }

      if (acc.inComment) {
        acc.lastChar = char
        if (verbose) {
          console.log({ i, char, lastChar: acc.lastChar }, 'In comment')
        }
        return acc
      }

      if (char === acc.str && acc.inExpression) {
        acc.lastChar = char
        acc.str = ''
        if (verbose) {
          console.log({ i, char, lastChar: acc.lastChar }, 'String ended')
        }
        return acc
      }

      if (acc.str) {
        acc.lastChar = char
        if (verbose) {
          console.log({ i, char, lastChar: acc.lastChar }, 'In string')
        }
        return acc
      }

      if ((char === '"' || char === "'" || char === '`') && acc.inExpression) {
        acc.lastChar = char
        acc.str = char
        if (verbose) {
          console.log({ i, char, lastChar: acc.lastChar }, 'String ended')
        }
        return acc
      }

      if (char === '{') {
        if (acc.inExpression) {
          acc.depth += 1
          if (verbose) {
            console.log({ i, char, lastChar: acc.lastChar }, 'Scope started')
          }
        } else if (acc.lastChar === '{') {
          acc.inExpression = true
          acc.entry = i + 1
          acc.depth = 2
          if (verbose) {
            console.log(
              { i, char, lastChar: acc.lastChar },
              'Expression started'
            )
          }
        } else {
          if (verbose) {
            console.log(
              { i, char, lastChar: acc.lastChar },
              'Possible expression start'
            )
          }
        }

        acc.lastChar = char
        return acc
      }

      if (char === '}') {
        if (acc.inExpression) {
          acc.depth -= 1

          if (acc.depth === 0 && acc.lastChar === '}') {
            acc.inExpression = false
            acc.foundExpressions.push([acc.entry, i - 1])
            acc.entry = -1
            acc.str = ''
            if (verbose) {
              console.log(
                { i, char, lastChar: acc.lastChar },
                'Expression ended'
              )
            }
          } else {
            if (verbose) {
              console.log(
                { i, char, lastChar: acc.lastChar },
                'Scope ended or possible expression end'
              )
            }
          }
        }

        acc.lastChar = char
        return acc
      }

      if (verbose) {
        console.log({ i, char, lastChar: acc.lastChar }, 'No-op')
      }

      acc.lastChar = char
      return acc
    },
    {
      depth: 0,
      inExpression: false,
      inComment: false,
      foundExpressions: [],
      lastChar: '',
      entry: 0,
      str: '',
    }
  ).foundExpressions
}
